import {
  BUILDING_SECTION_DESCRIPTIONS,
  withDescriptions,
} from './sectionDescriptions'
import {
  calc,
  geo,
  round0,
  round1,
  type SectionField,
  type SectionSpec,
} from './sectionFields'
import { historySpec, marketSpec, sharedAnalysisSpecs } from './analysisShared'

/**
 * Building-analysis sections, declared against production
 * `building_analysis_public_share` rows.
 *
 * Note the storage asymmetry with land: a building share stores its selection
 * as an OBJECT (`{"summary":true}`), not an array. `buildBuildingAiPayload`
 * normalises that; nothing here depends on it.
 *
 * Unlike a land share, a building's `purchase_listings`, `rental_listings` and
 * `transactions` sections hold AGGREGATE ACTIVITY STATS for the building — not
 * a list of saved documents. None of the 27 production shares had selected
 * them, so their shape comes from the orchestrator that writes them.
 *
 * `feasibility` is excluded by product policy and is reported in
 * `sections_omitted`. `history` carries the building's own listings.
 */

/**
 * A parcel under the building. Owner identity never crosses: `owners` and
 * `owner_list` hold party names (natural persons on private parcels) and are
 * excluded by policy. The counts beside them are aggregate and safe, and the
 * transaction/permit COUNTS convey the same activity signal the arrays would.
 */
const landRows = (key: string): SectionField => ({
  path: 'lands',
  key,
  source: 'registre_foncier',
  kind: 'series',
  item: [
    { path: 'code_number', key: 'parcel_number', source: 'registre_foncier' },
    { path: 'egrid_nb', key: 'egrid', source: 'registre_foncier' },
    {
      path: 'area',
      key: 'area_m2',
      unit: 'm2',
      source: 'registre_foncier',
      transform: round1,
    },
    calc('cover_area_perc', 'built_share_pct', 'pct'),
    calc('private_owner_count', 'private_owner_count', 'count'),
    calc('institutional_owner_count', 'institutional_owner_count', 'count'),
    calc('public_owner_count', 'public_owner_count', 'count'),
    calc('transaction_count', 'transaction_count', 'count'),
    {
      path: 'transaction_last_year',
      key: 'last_transaction_year',
      unit: 'year',
      source: 'registre_foncier',
    },
    calc('construction_permit_count', 'construction_permit_count', 'count'),
    {
      path: 'construction_permit_last_year',
      key: 'last_construction_permit_year',
      unit: 'year',
      source: 'geoportal',
    },
  ],
})

/** RegBL heating and energy attributes, shared by several sections. */
const energyFields: SectionField[] = [
  { path: 'heater_generator', key: 'heating_generator', source: 'geoportal' },
  {
    path: 'power_source_heater',
    key: 'heating_energy_source',
    source: 'geoportal',
  },
  {
    path: 'heater_water_generator',
    key: 'water_heating_generator',
    source: 'geoportal',
  },
  {
    path: 'power_source_water_heater',
    key: 'water_heating_energy_source',
    source: 'geoportal',
  },
  {
    path: 'energy_reference_area',
    key: 'energy_reference_area_m2',
    unit: 'm2',
    source: 'geoportal',
    transform: round1,
  },
  {
    path: 'heat_consumption_index',
    key: 'heat_consumption_index',
    source: 'spatial_analysis',
  },
]

const BUILDING_SPECS: SectionSpec[] = [
  {
    id: 'characteristics',
    title: 'Building characteristics',
    provenance: {
      provider: 'Federal Register of Buildings and Dwellings (RegBL)',
    },
    fields: [
      { path: 'category', key: 'building_category', source: 'geoportal' },
      { path: 'type', key: 'building_type', source: 'geoportal' },
      { path: 'status', key: 'building_status', source: 'geoportal' },
      { path: 'classification', key: 'building_class', source: 'geoportal' },
      {
        path: 'popety_classification',
        key: 'popety_classification',
        source: 'spatial_analysis',
      },
      { path: 'principal_type', key: 'principal_type', source: 'geoportal' },
      {
        path: 'building_period',
        key: 'construction_period',
        source: 'geoportal',
      },
      {
        path: 'renovation_period',
        key: 'renovation_period',
        source: 'geoportal',
      },
      geo('building_year', 'construction_year', 'year'),
      geo('renovation_year', 'renovation_year', 'year'),
      calc('floor_nb', 'floor_nb', 'count'),
      calc('unit_nb', 'unit_nb', 'count'),
      calc('dwellings_nb', 'dwellings_nb', 'count'),
      {
        path: 'area',
        key: 'ground_floor_area_m2',
        unit: 'm2',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'gross_floor_area',
        key: 'gross_floor_area_m2',
        unit: 'm2',
        source: 'spatial_analysis',
        transform: round1,
      },
      {
        path: 'building_volume',
        key: 'volume_m3',
        unit: 'm3',
        source: 'geoportal',
        transform: round0,
      },
      {
        path: 'height',
        key: 'height_m',
        unit: 'm',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'height_estimate',
        key: 'height_estimate_m',
        unit: 'm',
        source: 'spatial_analysis',
        transform: round1,
      },
      {
        path: 'dwelling_median_area',
        key: 'median_dwelling_area_m2',
        unit: 'm2',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'dwelling_median_room_nb',
        key: 'median_dwelling_room_nb',
        unit: 'count',
        source: 'geoportal',
        transform: round1,
      },
      ...energyFields,
      {
        path: 'has_civil_protection_shelter',
        key: 'has_civil_protection_shelter',
        source: 'geoportal',
      },
      {
        path: 'protected_rank',
        key: 'heritage_protection_rank',
        source: 'geoportal',
      },
      { path: 'lake_view', key: 'lake_view', source: 'spatial_analysis' },
      {
        path: 'mountain_view',
        key: 'mountain_view',
        source: 'spatial_analysis',
      },
      { path: 'exposure', key: 'exposure', source: 'spatial_analysis' },
      // A category string ("more than 9 …"), not a number — the public API
      // types it as a nullable string.
      { path: 'slope', key: 'slope', source: 'geoportal' },
      {
        path: 'public_transport_quality',
        key: 'public_transport_quality',
        source: 'geoportal',
      },
      { path: 'noise_exposure', key: 'noise_exposure', source: 'geoportal' },
      calc('solar_area', 'solar_suitable_roof_area_m2', 'm2'),
      calc('solar_global_exposure', 'solar_global_exposure_kwh', 'kWh'),
      {
        path: 'geo_portal_link',
        key: 'cantonal_geoportal_link',
        source: 'geoportal',
      },
    ],
  },
  {
    id: 'units',
    title: 'Dwellings',
    provenance: {
      provider: 'Federal Register of Buildings and Dwellings (RegBL)',
    },
    fields: [
      calc('total_units', 'total_count', 'count'),
      calc('dwellings_nb', 'dwellings_nb', 'count'),
      calc('unit_nb', 'unit_nb', 'count'),
      {
        path: 'dwelling_median_area',
        key: 'median_area_m2',
        unit: 'm2',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'dwelling_median_room_nb',
        key: 'median_room_nb',
        unit: 'count',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'buildings_administrative',
        key: 'administrative_buildings',
        source: 'geoportal',
        kind: 'series',
        item: [
          { path: 'egid', key: 'egid', source: 'geoportal' },
          { path: 'name', key: 'name', source: 'geoportal' },
          { path: 'category', key: 'building_category', source: 'geoportal' },
          { path: 'status', key: 'building_status', source: 'geoportal' },
          geo('building_year', 'construction_year', 'year'),
          geo('renovation_year', 'renovation_year', 'year'),
          calc('floor_nb', 'floor_nb', 'count'),
          calc('unit_nb', 'unit_nb', 'count'),
          {
            path: 'area',
            key: 'ground_floor_area_m2',
            unit: 'm2',
            source: 'geoportal',
            transform: round1,
          },
          {
            path: 'building_volume',
            key: 'volume_m3',
            unit: 'm3',
            source: 'geoportal',
            transform: round0,
          },
        ],
      },
    ],
  },
  {
    id: 'energy',
    title: 'Energy',
    provenance: {
      provider: 'Federal Register of Buildings and Dwellings (RegBL)',
    },
    fields: [
      {
        path: 'energy_reference_area',
        key: 'energy_reference_area_m2',
        unit: 'm2',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'gross_floor_area',
        key: 'gross_floor_area_m2',
        unit: 'm2',
        source: 'spatial_analysis',
        transform: round1,
      },
      geo('renovation_year', 'renovation_year', 'year'),
    ],
  },
  {
    id: 'land',
    title: 'Parcels under the building',
    provenance: { provider: 'Swiss land registry' },
    fields: [
      {
        path: 'total_land_area',
        key: 'total_land_area_m2',
        unit: 'm2',
        source: 'registre_foncier',
        transform: round1,
      },
      landRows('parcels'),
    ],
  },
  {
    id: 'potential',
    title: 'Development potential',
    provenance: { provider: 'Swiss land registry' },
    fields: [
      calc('floor_nb', 'floor_nb', 'count'),
      {
        path: 'gross_floor_area',
        key: 'gross_floor_area_m2',
        unit: 'm2',
        source: 'spatial_analysis',
        transform: round1,
      },
      {
        path: 'height',
        key: 'height_m',
        unit: 'm',
        source: 'geoportal',
        transform: round1,
      },
      {
        path: 'height_estimate',
        key: 'height_estimate_m',
        unit: 'm',
        source: 'spatial_analysis',
        transform: round1,
      },
      landRows('parcels'),
    ],
  },
  {
    id: 'building_permits',
    title: 'Construction permits',
    provenance: { provider: 'Cantonal building-permit publications' },
    fields: [landRows('parcels')],
  },
  {
    id: 'purchase_listings',
    title: 'Sale listing activity',
    fields: [
      calc('listing_sale_number', 'listing_count', 'count'),
      {
        path: 'listing_sale_last_year',
        key: 'last_listed_year',
        unit: 'year',
        source: 'spatial_analysis',
      },
    ],
  },
  {
    id: 'rental_listings',
    title: 'Rental listing activity',
    fields: [
      calc('listing_rent_number', 'listing_count', 'count'),
      {
        path: 'listing_rent_last_year',
        key: 'last_listed_year',
        unit: 'year',
        source: 'spatial_analysis',
      },
    ],
  },
  {
    id: 'transactions',
    title: 'Transaction activity',
    provenance: { provider: 'Swiss land registry' },
    fields: [
      calc('lands_transaction_number', 'transaction_count', 'count'),
      {
        path: 'transaction_last_year',
        key: 'last_transaction_year',
        unit: 'year',
        source: 'registre_foncier',
      },
      landRows('parcels'),
    ],
  },
  {
    id: 'notes',
    title: 'Notes',
    fields: [
      {
        path: '',
        key: 'notes',
        source: 'user',
        kind: 'series',
        item: [
          { path: 'create_timestamp', key: 'written_on', source: 'user' },
          { path: 'author_name', key: 'author', source: 'user' },
          { path: 'content', key: 'content', source: 'user' },
        ],
      },
    ],
  },
  {
    id: 'files',
    title: 'Files',
    fields: [
      {
        path: '',
        key: 'files',
        source: 'user',
        kind: 'series',
        item: [
          { path: 'name', key: 'name', source: 'user' },
          { path: 'type', key: 'type', source: 'user' },
          { path: 'size', key: 'size_bytes', source: 'user' },
          { path: 'create_timestamp', key: 'added_on', source: 'user' },
        ],
      },
    ],
  },
  marketSpec('purchase_market', 'purchase', 'Purchase market'),
  marketSpec('rental_market', 'rent', 'Rental market'),
  // A building share's history carries listings only — no transactions or
  // permits array is written onto it.
  historySpec({ transactions: false, permits: false }),
  ...sharedAnalysisSpecs(),
]

export const BUILDING_SECTION_SPECS: SectionSpec[] = withDescriptions(
  BUILDING_SPECS,
  BUILDING_SECTION_DESCRIPTIONS
)
